import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2, Plus, Video, Users, BookOpen, Trash2, Play,
  Settings, FileText, CheckCircle2, AlertCircle, Trash, Shield, ChevronDown, Send, GripVertical
} from "lucide-react";
import { useCallback } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import SecuritySettingsPanel from "./SecuritySettingsPanel";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type LibraryItem = {
  id: string; name: string; storage_path: string; created_at: string;
  title?: string | null; notes?: string | null; explanation?: string | null;
};

type PremiumGroup = {
  id: string; name: string; description: string; start_date: string | null; is_active: boolean;
  price_cents?: number; currency?: string; max_seats?: number | null; enrollment_deadline?: string | null;
  premium_enrollments?: { id?: string; count?: number }[];
  security_settings?: Record<string, unknown> | null;
  telegram_group_chat_id?: number | string | null;
};

type PremiumLesson = {
  id: string; title: string; day_index: number; notes: string; explanation: string;
  premium_video_library?: LibraryItem;
};

type Props = {
  sessionToken: string;
};

// Sortable row for drag-and-drop lesson reordering
const SortableLessonRow = ({
  lesson,
  onDelete,
}: {
  lesson: PremiumLesson;
  onDelete: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: lesson.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <TableRow ref={setNodeRef} style={style} className="bg-card">
      <TableCell className="py-2 w-8">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      <TableCell className="py-2 text-xs font-bold w-12 text-muted-foreground">
        Day {lesson.day_index + 1}
      </TableCell>
      <TableCell className="py-2 text-sm">{lesson.title}</TableCell>
      <TableCell className="py-2 text-right">
        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={onDelete}>
          <Trash className="w-3 h-3" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const PremiumTab = ({ sessionToken }: Props) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [activeTab, setActiveTab] = useState("groups");
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [groups, setGroups] = useState<PremiumGroup[]>([]);
  const [lessons, setLessons] = useState<Record<string, PremiumLesson[]>>({});
  const [loading, setLoading] = useState(false);
  
  // Dialog States
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [newGroupPrice, setNewGroupPrice] = useState("0");
  const [newGroupCurrency, setNewGroupCurrency] = useState("USD");
  const [newGroupMaxSeats, setNewGroupMaxSeats] = useState("");
  const [newGroupDeadline, setNewGroupDeadline] = useState("");
  
  const [showEnroll, setShowEnroll] = useState<PremiumGroup | null>(null);
  const [enrollEmails, setEnrollEmails] = useState("");
  
  const [selectedGroupForLessons, setSelectedGroupForLessons] = useState<PremiumGroup | null>(null);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [lessonForm, setLessonForm] = useState({ title: "", videoId: "", dayIndex: 0, notes: "", explanation: "" });
  const [editingLibrary, setEditingLibrary] = useState<LibraryItem | null>(null);
  const [libraryForm, setLibraryForm] = useState({ title: "", notes: "", explanation: "" });
  const [deletingGroup, setDeletingGroup] = useState<PremiumGroup | null>(null);

  // Per-group Telegram chat-id draft state (keyed by group.id) — for the
  // "Verified Group Invite" section.
  const [tgChatDraft, setTgChatDraft] = useState<Record<string, string>>({});
  const [tgSaving, setTgSaving] = useState<Record<string, boolean>>({});

  const handleSaveTelegramChatId = async (group: PremiumGroup) => {
    const raw = (tgChatDraft[group.id] ?? "").trim();
    setTgSaving(p => ({ ...p, [group.id]: true }));
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "manage_premium_groups",
          subAction: "update",
          sessionToken,
          groupId: group.id,
          telegramGroupChatId: raw === "" ? null : raw,
        },
      });
      if (res.error || !res.data?.success) {
        throw new Error(res.data?.error || res.error?.message || "Save failed");
      }
      toast({
        title: raw ? "Verified group linked" : "Verified group cleared",
        description: raw
          ? "New verified students will be auto-invited to this Telegram group."
          : "Will fall back to the global verified students group.",
      });
      await fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setTgSaving(p => ({ ...p, [group.id]: false }));
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: libData } = await supabase.functions.invoke("manage-certificates", { 
        body: { action: "list_premium_library", sessionToken } 
      });
      if (libData?.library) setLibrary(libData.library);

      const { data: grpData } = await supabase.functions.invoke("manage-certificates", { 
        body: { action: "list_premium_groups", sessionToken } 
      });
      if (grpData?.groups) setGroups(grpData.groups);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchLessons = async (groupId: string) => {
    try {
      const { data } = await supabase.functions.invoke("manage-certificates", { 
        body: { action: "manage_premium_lessons", subAction: "list", sessionToken, groupId } 
      });
      if (data?.lessons) {
        setLessons(prev => ({ ...prev, [groupId]: data.lessons }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleLessonDragEnd = async (groupId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const current = lessons[groupId] || [];
    const oldIdx = current.findIndex(l => l.id === active.id);
    const newIdx = current.findIndex(l => l.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(current, oldIdx, newIdx).map((l, i) => ({ ...l, day_index: i }));
    // Optimistic UI
    setLessons(prev => ({ ...prev, [groupId]: reordered }));
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "manage_premium_lessons",
          subAction: "reorder",
          sessionToken,
          groupId,
          orderedLessonIds: reordered.map(l => l.id),
        },
      });
      if (res.error || !res.data?.success) {
        throw new Error(res.data?.error || res.error?.message || "Reorder failed");
      }
      toast({ title: "Lesson order saved" });
    } catch (err) {
      const error = err as Error;
      toast({ title: "Reorder failed", description: error.message, variant: "destructive" });
      // Revert on failure
      fetchLessons(groupId);
    }
  };

  const handleUploadVideo = async (file: File) => {
    setLoading(true);
    try {
      // 1. Get Signed URL
      const { data: signData } = await supabase.functions.invoke("manage-certificates", {
        body: { action: "get_upload_url", sessionToken, lessonId: "library", fileName: file.name }
      });
      if (!signData?.path) throw new Error("Failed to get upload path");

      // 2. Upload to Storage
      const { error: uploadError } = await supabase.storage.from("course-videos").uploadToSignedUrl(signData.path, signData.token, file);
      if (uploadError) throw uploadError;

      // 3. Register in DB
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "add_to_premium_library", sessionToken, name: file.name, path: signData.path }
      });

      toast({ title: "Video added to library" });
      fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { 
          action: "manage_premium_groups", subAction: "add", sessionToken,
          name: newGroupName, description: newGroupDesc,
          priceCents: Math.round(parseFloat(newGroupPrice || "0") * 100),
          currency: newGroupCurrency,
          maxSeats: newGroupMaxSeats ? parseInt(newGroupMaxSeats) : null,
          enrollmentDeadline: newGroupDeadline ? new Date(newGroupDeadline).toISOString() : null,
        }
      });
      
      if (res.error || (res.data && res.data.success === false)) {
        throw new Error(res.data?.error || res.error?.message || "Failed to create group.");
      }
      
      setShowAddGroup(false); 
      setNewGroupName(""); setNewGroupDesc("");
      setNewGroupPrice("0"); setNewGroupCurrency("USD");
      setNewGroupMaxSeats(""); setNewGroupDeadline("");
      await fetchData();
      toast({ title: "✅ Group created!", description: "You can now add lessons to this group." });
    } catch (err) {
      const error = err as Error;
      console.error("Group creation failed:", error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleStartGroup = async (groupId: string) => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "manage_premium_groups", subAction: "start", sessionToken, groupId }
      });
      if (res.data?.error || !res.data?.success) throw new Error(res.data?.error || "Failed to start course.");
      
      toast({ title: "Course started for group!" });
      fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleEnroll = async () => {
    if (!showEnroll) return;
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "enroll_premium_student", sessionToken, groupId: showEnroll.id, emails: enrollEmails }
      });
      if (res.data?.error || !res.data?.success) throw new Error(res.data?.error || "Enrollment failed.");
      
      toast({ title: "Students enrolled" });
      setShowEnroll(null); setEnrollEmails("");
      fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleAddLesson = async () => {
    if (!selectedGroupForLessons) return;
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { 
          action: "manage_premium_lessons", subAction: "add", sessionToken, 
          groupId: selectedGroupForLessons.id, ...lessonForm 
        }
      });
      setShowAddLesson(false); setLessonForm({ title: "", videoId: "", dayIndex: 0, notes: "", explanation: "" });
      fetchLessons(selectedGroupForLessons.id);
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleSelectLibraryVideo = (v: LibraryItem) => {
    setLessonForm(p => ({
      ...p,
      videoId: v.id,
      title: p.title || v.title || v.name,
      notes: p.notes || v.notes || "",
      explanation: p.explanation || v.explanation || "",
    }));
  };

  const handleSaveLibraryDetails = async () => {
    if (!editingLibrary) return;
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_premium_library", sessionToken, videoId: editingLibrary.id, ...libraryForm }
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Update failed");
      toast({ title: "Library updated" });
      setEditingLibrary(null);
      fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteLibraryVideo = async (v: LibraryItem) => {
    if (!(await confirm(`Delete "${v.name}" from the library? This cannot be undone.`))) return;
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_from_premium_library", sessionToken, videoId: v.id }
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Delete failed");
      toast({ title: "Video removed" });
      fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "manage_premium_groups", subAction: "delete", sessionToken, groupId: deletingGroup.id }
      });
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Delete failed");
      toast({ title: "Group deleted" });
      setDeletingGroup(null);
      fetchData();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Premium Course Management</h2>
          <p className="text-muted-foreground">Manage cohort-based premium groups, library assets, and drip schedules.</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="groups"><Users className="w-4 h-4 mr-2" /> Groups & Enrollments</TabsTrigger>
          <TabsTrigger value="library"><Video className="w-4 h-4 mr-2" /> Video Library</TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="space-y-4 pt-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddGroup(true)}><Plus className="w-4 h-4 mr-2" /> Create New Group</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {groups.map(group => (
              <Card key={group.id} className="border-l-4 border-l-amber-500">
                <CardHeader>
                  <div className="flex justify-between">
                    <div>
                      <CardTitle className="text-xl">{group.name}</CardTitle>
                      <CardDescription>{group.description || "No description"}</CardDescription>
                    </div>
                    <Badge variant={group.is_active ? "default" : "secondary"}>
                      {group.is_active ? "In Progress" : "Draft"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4 text-sm bg-muted/30 p-3 rounded-lg">
                    <div><p className="text-muted-foreground">Start Date</p><p className="font-bold">{group.start_date ? new Date(group.start_date).toLocaleDateString() : "Not scheduled"}</p></div>
                    <div className="text-right"><p className="text-muted-foreground">Enrolled</p><p className="font-bold">{group.premium_enrollments?.length || 0}{group.max_seats ? ` / ${group.max_seats}` : ""} Students</p></div>
                    <div className="col-span-2 text-right"><p className="text-muted-foreground">Deadline</p><p className="font-bold">{group.enrollment_deadline ? new Date(group.enrollment_deadline).toLocaleDateString() : "—"}</p></div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold flex items-center gap-1"><BookOpen className="w-4 h-4" /> Lesson Sequence</p>
                      {(lessons[group.id]?.length || 0) > 1 && (
                        <span className="text-[10px] text-muted-foreground">Drag <GripVertical className="inline w-3 h-3" /> to reorder</span>
                      )}
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => handleLessonDragEnd(group.id, e)}
                      >
                        <Table>
                          <TableBody>
                            <SortableContext
                              items={(lessons[group.id] || []).map(l => l.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {(lessons[group.id] || []).map((l) => (
                                <SortableLessonRow
                                  key={l.id}
                                  lesson={l}
                                  onDelete={() => {
                                    supabase.functions.invoke("manage-certificates", { body: { action: "manage_premium_lessons", subAction: "delete", sessionToken, lessonId: l.id } }).then(() => fetchLessons(group.id));
                                  }}
                                />
                              ))}
                            </SortableContext>
                            <TableRow>
                              <TableCell colSpan={4} className="p-0">
                                <Button variant="ghost" className="w-full text-xs py-2 h-auto text-primary" onClick={() => { setSelectedGroupForLessons(group); setShowAddLesson(true); if (!lessons[group.id]) fetchLessons(group.id); }}>
                                  <Plus className="w-3 h-3 mr-1" /> Add Lesson
                                </Button>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </DndContext>
                    </div>
                    {!lessons[group.id] && (
                      <Button variant="outline" size="sm" className="w-full" onClick={() => fetchLessons(group.id)}>
                        Load lessons
                      </Button>
                    )}
                  </div>

                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 hover:bg-muted transition-colors text-sm">
                        <span className="flex items-center gap-2 font-medium">
                          <Shield className="w-4 h-4 text-primary" /> Security Settings
                        </span>
                        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <SecuritySettingsPanel
                        sessionToken={sessionToken}
                        targetType="premium_group"
                        targetId={group.id}
                        initialSettings={group.security_settings as never}
                        onSaved={() => fetchData()}
                      />
                    </CollapsibleContent>
                  </Collapsible>

                  <Collapsible>
                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 hover:bg-muted transition-colors text-sm">
                        <span className="flex items-center gap-2 font-medium">
                          <Send className="w-4 h-4 text-primary" /> Verified Group Invite
                          {group.telegram_group_chat_id ? (
                            <Badge variant="default" className="ml-1 text-[10px] py-0 h-4">Linked</Badge>
                          ) : (
                            <Badge variant="secondary" className="ml-1 text-[10px] py-0 h-4">Fallback</Badge>
                          )}
                        </span>
                        <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="border border-border rounded-md p-3 space-y-3 bg-muted/20">
                        <div className="text-xs text-muted-foreground space-y-2 leading-relaxed">
                          <p>
                            <strong className="text-foreground">How it works:</strong> When a student is enrolled in this cohort and signs up (or is already linked), the system queues a one-time Telegram invite. A cron worker DMs them a single-use invite link to the group below within ~5 minutes.
                          </p>
                          <ol className="list-decimal pl-4 space-y-1">
                            <li>Create a <strong>private Telegram group</strong> for this cohort.</li>
                            <li>Add your bot as an <strong>admin</strong> with the <em>“Invite Users via Link”</em> permission.</li>
                            <li>In the group, send <code className="bg-muted px-1 py-0.5 rounded">/id</code> (or use any chat-id bot) to get the group's <strong>chat_id</strong> — it starts with <code className="bg-muted px-1 py-0.5 rounded">-100…</code>.</li>
                            <li>Paste it below and save. New verified students will be auto-invited there.</li>
                          </ol>
                          <p className="text-[11px]">If left empty, this cohort uses the global fallback group (<code className="bg-muted px-1 py-0.5 rounded">verified_students_group</code>).</p>
                        </div>
                        <div className="flex gap-2 items-end">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Telegram Group chat_id</Label>
                            <Input
                              placeholder="-1001234567890"
                              value={tgChatDraft[group.id] ?? (group.telegram_group_chat_id?.toString() ?? "")}
                              onChange={e => setTgChatDraft(p => ({ ...p, [group.id]: e.target.value }))}
                              className="font-mono text-sm h-9"
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleSaveTelegramChatId(group)}
                            disabled={tgSaving[group.id]}
                          >
                            {tgSaving[group.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                          </Button>
                        </div>
                        {group.telegram_group_chat_id && (
                          <p className="text-[11px] text-muted-foreground">
                            Currently linked to: <code className="bg-muted px-1 py-0.5 rounded">{String(group.telegram_group_chat_id)}</code>
                          </p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" size="sm" onClick={() => setShowEnroll(group)}>
                      <Users className="w-4 h-4 mr-1" /> Enroll Students
                    </Button>
                    {!group.is_active ? (
                      <Button className="flex-1 bg-amber-600 hover:bg-amber-700" size="sm" onClick={() => handleStartGroup(group.id)}>
                        <Play className="w-4 h-4 mr-1" /> Start the Class
                      </Button>
                    ) : (
                      <Button variant="secondary" className="flex-1" size="sm" disabled>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Course Live
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => setDeletingGroup(group)} title="Delete group">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="library" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Video Library</CardTitle>
                <Button onClick={() => fileInputRef.current?.click()} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Upload Video
                </Button>
              </div>
              <CardDescription>All uploaded videos. These can be attached to any premium group sequence.</CardDescription>
            </CardHeader>
            <CardContent>
              <input type="file" ref={fileInputRef} className="hidden" accept="video/*" onChange={e => e.target.files?.[0] && handleUploadVideo(e.target.files[0])} />
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {library.map(file => (
                    <TableRow key={file.id}>
                      <TableCell className="font-medium">
                        <div>{file.title || file.name}</div>
                        {file.notes && <div className="text-xs text-muted-foreground line-clamp-1">{file.notes}</div>}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">{file.storage_path}</TableCell>
                      <TableCell>{new Date(file.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingLibrary(file);
                          setLibraryForm({ title: file.title || file.name, notes: file.notes || "", explanation: file.explanation || "" });
                        }}>
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteLibraryVideo(file)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {library.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">No videos in library yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sequences" className="hidden">
        </TabsContent>
      </Tabs>

      {/* Add Group Dialog */}
      <Dialog open={showAddGroup} onOpenChange={setShowAddGroup}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Premium Group</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Group Name</Label><Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="e.g., April Cohort 2024" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} placeholder="Enter group details..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Price</Label><Input type="number" min="0" step="0.01" value={newGroupPrice} onChange={e => setNewGroupPrice(e.target.value)} /></div>
              <div className="space-y-2"><Label>Currency</Label><Input value={newGroupCurrency} onChange={e => setNewGroupCurrency(e.target.value.toUpperCase())} maxLength={3} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Max Seats (optional)</Label><Input type="number" min="1" value={newGroupMaxSeats} onChange={e => setNewGroupMaxSeats(e.target.value)} placeholder="Unlimited" /></div>
              <div className="space-y-2"><Label>Enrollment Deadline (optional)</Label><Input type="datetime-local" value={newGroupDeadline} onChange={e => setNewGroupDeadline(e.target.value)} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddGroup(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim() || loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll Dialog */}
      <Dialog open={!!showEnroll} onOpenChange={open => !open && setShowEnroll(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enroll Students: {showEnroll?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Emails (one per line or comma separated)</Label>
              <Textarea 
                value={enrollEmails} 
                onChange={e => setEnrollEmails(e.target.value)} 
                placeholder="email1@example.com, email2@example.com..." 
                className="h-32 font-mono"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <p>Students with these emails will immediately gain access to the premium group content once the course starts.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEnroll(null)}>Cancel</Button>
            <Button onClick={handleEnroll} disabled={!enrollEmails.trim()}>Enroll Students</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lesson to Sequence Dialog */}
      <Dialog open={showAddLesson} onOpenChange={setShowAddLesson}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Lesson to Sequence</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 h-[400px]">
            <div className="space-y-4 overflow-y-auto pr-2">
              <div className="space-y-2"><Label>Title</Label><Input value={lessonForm.title} onChange={e => setLessonForm(p => ({ ...p, title: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Day Index (0 for Immediate)</Label><Input type="number" min="0" value={lessonForm.dayIndex} onChange={e => setLessonForm(p => ({ ...p, dayIndex: parseInt(e.target.value) }))} /></div>
              <div className="space-y-2"><Label>Short Notes</Label><Textarea value={lessonForm.notes} onChange={e => setLessonForm(p => ({ ...p, notes: e.target.value }))} className="h-20" /></div>
              <div className="space-y-2"><Label>Extended Explanation</Label><Textarea value={lessonForm.explanation} onChange={e => setLessonForm(p => ({ ...p, explanation: e.target.value }))} className="h-32" /></div>
            </div>
            <div className="space-y-2 border-l pl-4">
              <Label>Select Video from Library</Label>
              <div className="space-y-1 h-full overflow-y-auto">
                {library.map(v => (
                  <Button 
                    key={v.id} 
                    variant={lessonForm.videoId === v.id ? "default" : "outline"}
                    className="w-full justify-start h-auto py-2 px-3 text-left overflow-hidden" 
                    onClick={() => handleSelectLibraryVideo(v)}
                  >
                    <div>
                      <p className="text-sm font-medium">{v.title || v.name}</p>
                      {v.notes && <p className="text-[10px] opacity-70 mt-0.5 line-clamp-1">{v.notes}</p>}
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLesson(false)}>Cancel</Button>
            <Button onClick={handleAddLesson} disabled={!lessonForm.title || !lessonForm.videoId}>Save Lesson</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Library Video Dialog */}
      <Dialog open={!!editingLibrary} onOpenChange={open => !open && setEditingLibrary(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Video Details</DialogTitle>
            <CardDescription className="text-xs">These defaults will auto-fill when this video is selected for a lesson.</CardDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={libraryForm.title} onChange={e => setLibraryForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Short Notes</Label><Textarea value={libraryForm.notes} onChange={e => setLibraryForm(p => ({ ...p, notes: e.target.value }))} className="h-20" /></div>
            <div className="space-y-2"><Label>Extended Explanation</Label><Textarea value={libraryForm.explanation} onChange={e => setLibraryForm(p => ({ ...p, explanation: e.target.value }))} className="h-32" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLibrary(null)}>Cancel</Button>
            <Button onClick={handleSaveLibraryDetails}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Group Confirm Dialog */}
      <Dialog open={!!deletingGroup} onOpenChange={open => !open && setDeletingGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Premium Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>This will permanently delete <strong>{deletingGroup?.name}</strong>, all its lessons, and all student enrollments in this group. This cannot be undone.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingGroup(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteGroup}>Delete Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PremiumTab;
